import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Building2,
	ChevronDown,
	CircleAlert,
	CreditCard,
	ClipboardCheck,
	FileInput,
	HandCoins,
	Inbox,
	Landmark,
	LayoutDashboard,
	ListFilter,
	LoaderCircle,
	LogOut,
	PackageOpen,
	Receipt,
	ReceiptText,
	RefreshCw,
	ShieldCheck,
	Truck,
	Waypoints,
	WalletCards,
	Wheat,
} from "lucide-react";
import { useState } from "react";

import { authClient } from "@/auth-client";
import { api } from "@/client";
import { AccessView } from "@/features/access/AccessView";
import { Dashboard, type DashboardView } from "@/features/dashboard/Dashboard";
import { InboxView } from "@/features/inbox/InboxView";
import { MovementsView } from "@/features/movements/MovementsView";
import { PayablesView } from "@/features/payables/PayablesView";
import { CifView } from "@/features/cif/CifView";
import { CtesView } from "@/features/qive/CtesView";
import { InboundInvoicesView } from "@/features/qive/InboundInvoicesView";
import { FinancePresentation } from "@/presentation/finance";

type FinancialView =
	| DashboardView
	| "MOVEMENTS"
	| "INBOX"
	| "PAYABLES"
	| "INBOUND_INVOICES"
	| "CTES"
	| "CIF"
	| "ACCESS";

const viewTitles: Record<FinancialView, string> = {
	OVERVIEW: "Visão geral do mês",
	MOVEMENTS: "Conta principal de pagamentos",
	INBOX: "Caixa de entrada financeira",
	PAYABLES: "A Pagar",
	INBOUND_INVOICES: "Notas de entrada",
	CTES: "CT-es do mês",
	CIF: "CIF",
	ACCESS: "Acessos",
	SUGAR: "Pagamentos de açúcar",
	PACKAGING: "Pagamentos de embalagens",
	FREIGHT: "Fretes",
	GNRE: "Pagamentos de GNRE",
	CHECKS: "Cheques do mês",
	RECEIPTS: "Recebimentos do mês",
	TREASURY: "Contas da tesouraria",
	EXPENSES: "Despesas operacionais",
	SUPPLIERS: "Fornecedores",
};

const navigationGroups = [
	{
		items: [{ view: "OVERVIEW", label: "Dashboard", icon: LayoutDashboard }],
	},
	{
		label: "Operação",
		items: [
			{ view: "INBOX", label: "Caixa de entrada", icon: Inbox },
			{ view: "PAYABLES", label: "A Pagar", icon: WalletCards },
			{ view: "CIF", label: "CIF", icon: ClipboardCheck },
			{ view: "MOVEMENTS", label: "Movimentos", icon: ListFilter },
		],
	},
	{
		id: "documents",
		label: "Documentos",
		collapsible: true,
		items: [
			{ view: "INBOUND_INVOICES", label: "Notas de entrada", icon: FileInput },
			{ view: "CTES", label: "CT-es", icon: Waypoints },
		],
	},
	{
		id: "monthly-sections",
		label: "Recortes do mês",
		collapsible: true,
		items: [
			{ view: "RECEIPTS", label: "Recebimentos", icon: HandCoins },
			{ view: "SUGAR", label: "Açúcar", icon: Wheat },
			{ view: "PACKAGING", label: "Embalagens", icon: PackageOpen },
			{ view: "FREIGHT", label: "Fretes", icon: Truck },
			{ view: "GNRE", label: "GNRE", icon: ReceiptText },
			{ view: "CHECKS", label: "Cheques", icon: CreditCard },
			{ view: "EXPENSES", label: "Despesas operacionais", icon: Receipt },
		],
	},
	{
		id: "structure",
		label: "Estrutura",
		collapsible: true,
		items: [
			{ view: "TREASURY", label: "Contas", icon: Landmark },
			{ view: "SUPPLIERS", label: "Fornecedores", icon: Building2 },
			{ view: "ACCESS", label: "Acessos", icon: ShieldCheck, adminOnly: true },
		],
	},
] satisfies {
	id?: string;
	label?: string;
	collapsible?: boolean;
	items: {
		view: FinancialView;
		label: string;
		icon: typeof LayoutDashboard;
		adminOnly?: boolean;
	}[];
}[];

function AppError({ error }: { error: Error }) {
	return (
		<div className="grid min-h-screen place-items-center px-6">
			<div className="max-w-md rounded-3xl border border-red-200 bg-white p-8 text-center shadow-sm">
				<CircleAlert className="mx-auto mb-4 size-9 text-red-600" />
				<h1 className="text-xl font-semibold text-stone-900">Não foi possível abrir a fila</h1>
				<p className="mt-2 text-sm leading-6 text-stone-600">{error.message}</p>
			</div>
		</div>
	);
}

export function App() {
	const session = authClient.useSession();
	const [signingIn, setSigningIn] = useState(false);

	if (session.isPending) {
		return <FullScreenLoading label="Verificando seu acesso…" />;
	}
	if (!session.data) {
		const denied = new URLSearchParams(window.location.search).has("error");

		return (
			<div className="grid min-h-screen place-items-center bg-stone-50 px-6">
				<div className="w-full max-w-md rounded-3xl border border-stone-200 bg-white p-8 shadow-sm">
					<div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-800">
						<span className="grid size-9 place-items-center rounded-xl bg-emerald-950 text-white">
							S
						</span>
						SINDI Financeiro
					</div>
					<h1 className="mt-10 text-3xl font-semibold tracking-[-0.04em] text-stone-950">
						Entre com sua conta Google
					</h1>
					<p className="mt-3 text-sm leading-6 text-stone-600">
						Use o e-mail autorizado pela SINDI. Sua identidade será registrada automaticamente nas
						decisões financeiras.
					</p>
					{denied && (
						<p className="mt-4 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-800">
							Esta conta ainda não está autorizada. Peça acesso ao administrador.
						</p>
					)}
					<button
						type="button"
						disabled={signingIn}
						onClick={async () => {
							setSigningIn(true);
							await authClient.signIn.social({
								provider: "google",
								callbackURL: "/",
								errorCallbackURL: "/?error=access",
							});
							setSigningIn(false);
						}}
						className="mt-8 inline-flex h-12 w-full items-center justify-center gap-3 rounded-xl bg-emerald-950 px-5 text-sm font-semibold text-white transition hover:bg-emerald-900 disabled:opacity-60"
					>
						{signingIn ? <LoaderCircle className="size-5 animate-spin" /> : <GoogleMark />}
						Continuar com Google
					</button>
				</div>
			</div>
		);
	}

	return <AuthenticatedApp />;
}

function AuthenticatedApp() {
	const queryClient = useQueryClient();
	const [view, setView] = useState<FinancialView>("OVERVIEW");
	const [openNavigationGroups, setOpenNavigationGroups] = useState<string[]>([]);
	const meQuery = useQuery({
		queryKey: ["access", "me"],
		queryFn: () => api.access.me(),
	});
	const listQuery = useQuery({
		queryKey: ["transactions", "current-month"],
		queryFn: () => api.transactions.list(),
	});
	const syncMutation = useMutation({
		mutationFn: () => api.sync.run(),
		onSettled: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["transactions", "current-month"] }),
				queryClient.invalidateQueries({ queryKey: ["dae-payments", "current-month"] }),
				queryClient.invalidateQueries({ queryKey: ["receipts", "current-month"] }),
				queryClient.invalidateQueries({ queryKey: ["treasury", "current-month"] }),
				queryClient.invalidateQueries({ queryKey: ["inbox"] }),
				queryClient.invalidateQueries({ queryKey: ["payables"] }),
				queryClient.invalidateQueries({ queryKey: ["qive-archive"] }),
				queryClient.invalidateQueries({ queryKey: ["cif", "current-month"] }),
			]);
		},
	});

	if (listQuery.isPending || meQuery.isPending) {
		return <FullScreenLoading label="Abrindo o mês atual…" />;
	}

	const error = listQuery.error ?? meQuery.error;

	if (error) {
		return <AppError error={error} />;
	}
	if (!listQuery.data || !meQuery.data) {
		return <AppError error={new Error("A sessão não retornou os dados esperados.")} />;
	}

	const data = listQuery.data;
	const operator = meQuery.data;

	return (
		<div className="mx-auto min-h-screen max-w-[1800px] lg:grid lg:grid-cols-[232px_minmax(0,1fr)]">
			<aside className="border-b border-stone-200 bg-white/70 backdrop-blur lg:sticky lg:top-0 lg:h-screen lg:border-r lg:border-b-0">
				<div className="flex h-full items-center gap-4 px-4 py-3 sm:px-6 lg:min-h-0 lg:flex-col lg:items-stretch lg:gap-0 lg:px-4 lg:py-4">
					<div className="flex shrink-0 items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-800">
						<span className="grid size-7 place-items-center rounded-lg bg-emerald-950 text-white">
							S
						</span>
						<span className="hidden sm:inline">SINDI Financeiro</span>
					</div>

					<nav
						aria-label="Navegação principal"
						className="flex min-w-0 flex-1 gap-1 overflow-x-auto lg:mt-5 lg:min-h-0 lg:flex-col lg:gap-0.5 lg:overflow-x-hidden lg:overflow-y-auto lg:pr-1 lg:pl-px"
					>
						{navigationGroups.map((group) => {
							const items = group.items.filter(
								(item) => !("adminOnly" in item) || !item.adminOnly || operator.role === "ADMIN",
							);
							const collapsible = "collapsible" in group && group.collapsible;
							const open = !collapsible || openNavigationGroups.includes(group.id);
							const hasActiveItem = items.some((item) => item.view === view);

							return (
								<div
									key={group.label ?? "Dashboard"}
									className="contents lg:flex lg:flex-col lg:gap-0.5"
								>
									{group.label &&
										(collapsible ? (
											<button
												type="button"
												aria-expanded={open}
												aria-controls={`navigation-${group.id}`}
												onClick={() =>
													setOpenNavigationGroups((current) =>
														current.includes(group.id)
															? current.filter((id) => id !== group.id)
															: [...current, group.id],
													)
												}
												className="navigation-group-label mt-2 hidden h-6 w-full shrink-0 items-center px-2.5 uppercase tracking-[0.14em] text-stone-500 transition hover:text-stone-800 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-700 lg:flex"
											>
												<span className="truncate text-left">{group.label}</span>
												<span className="ml-auto flex items-center gap-2">
													{!open && hasActiveItem && (
														<span
															aria-hidden="true"
															className="size-1.5 rounded-full bg-emerald-700"
														/>
													)}
													<ChevronDown
														aria-hidden="true"
														className={`size-3 transition-transform ${open ? "" : "-rotate-90"}`}
													/>
												</span>
												{!open && hasActiveItem && (
													<span className="sr-only">contém a seção atual</span>
												)}
											</button>
										) : (
											<p className="mt-2 hidden h-6 shrink-0 items-center px-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500 lg:flex">
												{group.label}
											</p>
										))}
									<div
										id={"id" in group ? `navigation-${group.id}` : undefined}
										className={`contents ${open ? "lg:flex lg:flex-col lg:gap-0.5" : "lg:hidden"}`}
									>
										{items.map(({ view: itemView, label, icon: Icon }) => (
											<NavigationButton
												key={itemView}
												active={view === itemView}
												onClick={() => setView(itemView)}
												icon={<Icon className="size-4 shrink-0 lg:size-3" />}
											>
												{label}
											</NavigationButton>
										))}
									</div>
								</div>
							);
						})}
					</nav>

					<div className="ml-auto flex shrink-0 items-center gap-2 lg:mt-auto lg:ml-0 lg:border-t lg:border-stone-200 lg:pt-4">
						<div className="hidden min-w-0 lg:block">
							<p className="truncate text-sm font-semibold text-stone-900">{operator.name}</p>
							<p className="truncate text-xs text-stone-500">{operator.email}</p>
						</div>
						<button
							type="button"
							onClick={() => authClient.signOut()}
							title="Sair"
							aria-label="Sair"
							className="grid size-9 place-items-center rounded-xl text-stone-500 transition hover:bg-stone-100 hover:text-stone-900 lg:ml-auto"
						>
							<LogOut className="size-4" />
						</button>
					</div>
				</div>
			</aside>

			<main className="min-w-0 px-4 py-5 sm:px-6 lg:px-8 lg:py-8 xl:px-10">
				<header className="mb-6 flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
					<div>
						<h1 className="text-3xl font-semibold tracking-[-0.04em] text-stone-950 sm:text-4xl">
							{viewTitles[view]}
						</h1>
						<p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-stone-600">
							{view === "INBOX" ? (
								<span>Candidatos da Qive e da equipe antes do reconhecimento da obrigação</span>
							) : view === "PAYABLES" ? (
								<span>Obrigações abertas, aguardando banco ou pagas e conciliadas</span>
							) : view === "SUPPLIERS" ? (
								<span>Cadastro comum para reconhecimento e classificação dos pagamentos</span>
							) : view === "TREASURY" ? (
								<span>Livro próprio de contas, transferências e saldos</span>
							) : view === "INBOUND_INVOICES" ? (
								<span>NF-es destinadas à SINDI no mês corrente</span>
							) : view === "CTES" ? (
								<span>CT-es associados à SINDI, preservando seu papel documental</span>
							) : view === "CIF" ? (
								<span>Entregas, frete, descarga, cheque, caixa e CT-es do fluxo CIF</span>
							) : view === "ACCESS" ? (
								<span>Contas Google autorizadas, papéis e sessões da equipe</span>
							) : (
								<>
									<span className="font-medium text-stone-800">Itaú 58000</span>
									<span aria-hidden="true">·</span>
									<span className="capitalize">{data.month.label}</span>
									{data.coverage?.finishedAt && (
										<>
											<span aria-hidden="true">·</span>
											<span>Atualizado {FinancePresentation.moment(data.coverage.finishedAt)}</span>
										</>
									)}
								</>
							)}
						</p>
					</div>

					{view !== "ACCESS" && (
						<button
							type="button"
							onClick={() => syncMutation.mutate()}
							disabled={syncMutation.isPending}
							className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-emerald-950 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-900 disabled:cursor-wait disabled:opacity-60"
						>
							<RefreshCw className={`size-4 ${syncMutation.isPending ? "animate-spin" : ""}`} />
							{syncMutation.isPending ? "Atualizando…" : "Atualizar dados"}
						</button>
					)}
				</header>

				{view === "MOVEMENTS" ? (
					<MovementsView data={data} />
				) : view === "INBOX" ? (
					<InboxView />
				) : view === "PAYABLES" ? (
					<PayablesView />
				) : view === "INBOUND_INVOICES" ? (
					<InboundInvoicesView />
				) : view === "CTES" ? (
					<CtesView />
				) : view === "CIF" ? (
					<CifView />
				) : view === "ACCESS" ? (
					<AccessView />
				) : (
					<Dashboard data={data} view={view} />
				)}
			</main>
		</div>
	);
}

function FullScreenLoading({ label }: { label: string }) {
	return (
		<div className="grid min-h-screen place-items-center text-stone-600">
			<div className="flex items-center gap-3 text-sm font-medium">
				<LoaderCircle className="size-5 animate-spin" />
				{label}
			</div>
		</div>
	);
}

function GoogleMark() {
	return (
		<svg aria-hidden="true" viewBox="0 0 24 24" className="size-5">
			<path
				fill="currentColor"
				d="M21.8 12.2c0-.7-.1-1.5-.2-2.2H12v4h5.5a4.7 4.7 0 0 1-2 3.1v2.6h3.3c1.9-1.8 3-4.4 3-7.5Z"
			/>
			<path
				fill="currentColor"
				d="M12 22c2.7 0 5-.9 6.7-2.3l-3.3-2.6c-.9.6-2.1 1-3.4 1a5.9 5.9 0 0 1-5.5-4.1H3.1v2.7A10 10 0 0 0 12 22Z"
				opacity=".8"
			/>
			<path
				fill="currentColor"
				d="M6.5 14a6 6 0 0 1 0-3.9V7.4H3.1a10 10 0 0 0 0 9.3L6.5 14Z"
				opacity=".65"
			/>
			<path
				fill="currentColor"
				d="M12 5.9c1.5 0 2.8.5 3.8 1.5l2.9-2.9A9.7 9.7 0 0 0 3.1 7.4l3.4 2.7A5.9 5.9 0 0 1 12 5.9Z"
				opacity=".9"
			/>
		</svg>
	);
}

function NavigationButton({
	active,
	onClick,
	icon,
	children,
}: {
	active: boolean;
	onClick: () => void;
	icon: React.ReactNode;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-current={active ? "page" : undefined}
			className={`navigation-item inline-flex min-w-0 shrink-0 items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-700 lg:h-8 lg:w-full lg:gap-1.5 lg:rounded-lg lg:px-1.5 lg:py-0 ${active ? "bg-emerald-950 text-white shadow-sm lg:shadow-none" : "text-stone-500 hover:bg-stone-100 hover:text-stone-900 lg:text-stone-600"}`}
		>
			{icon}
			<span className="truncate">{children}</span>
		</button>
	);
}
